import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { getAvatarColor, getInitials } from '@/lib/avatar-utils'
import { cn, formatFullName } from '@/lib/utils'

interface UserAvatarProps {
  firstName?: string
  lastName?: string
  size?: 'sm' | 'md'
  tooltip?: boolean
  className?: string
}

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
}

/**
 * Colored initials avatar for a user. The User proto has no avatar URL, so
 * this always renders the AvatarFallback initials.
 */
export function UserAvatar({ firstName, lastName, size = 'md', tooltip = false, className }: UserAvatarProps) {
  const fullName = formatFullName(firstName, lastName)
  const initials = getInitials(firstName, lastName)
  const avatarColor = getAvatarColor(fullName)

  const avatar = (
    <Avatar className={cn(SIZE_CLASSES[size], className)}>
      <AvatarFallback className={cn(avatarColor, 'text-white font-semibold select-none')}>
        {initials}
      </AvatarFallback>
    </Avatar>
  )

  if (!tooltip) return avatar

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{avatar}</TooltipTrigger>
        <TooltipContent side="bottom">{fullName}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
